import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Plus, Trash2, DollarSign, TrendingUp, Loader2, Globe } from "lucide-react";
import { formatSydney, formatTableDateSydney, getCurrentSydneyDisplay } from "../../utils/timezone";

export default function StaffSignups() {
  const { api } = useAuth();
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [serverTime, setServerTime] = useState(null);
  
  const [form, setForm] = useState({
    date: new Date(),
    account_name: "",
    client_name: "",
    amount: "",
    description: "",
  });

  const fetchSignups = async () => {
    try {
      const response = await api().get("/signups");
      setSignups(response.data);
    } catch (error) {
      toast.error("Failed to load signups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSignups();
  }, []);

  const resetForm = () => {
    setForm({
      date: new Date(),
      account_name: "",
      client_name: "",
      amount: "",
      description: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.account_name || !form.client_name || !form.amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      await api().post("/signups", {
        date: format(form.date, "yyyy-MM-dd"),
        account_name: form.account_name,
        client_name: form.client_name,
        amount: parseFloat(form.amount),
        description: form.description,
      });
      toast.success("Signup recorded!");
      setDialogOpen(false);
      resetForm();
      fetchSignups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save signup");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this signup entry?")) return;
    try {
      await api().delete(`/signups/${id}`);
      toast.success("Signup deleted");
      fetchSignups();
    } catch (error) {
      toast.error("Failed to delete signup");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const totalAmount = signups.reduce((sum, s) => sum + (s.amount || 0), 0);
  const accountGroups = signups.reduce((acc, s) => {
    if (!acc[s.account_name]) acc[s.account_name] = { count: 0, amount: 0 };
    acc[s.account_name].count++;
    acc[s.account_name].amount += s.amount || 0;
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="staff-signups">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Signup Tracker</h1>
          <p className="text-slate-500 mt-1">Track your signup amounts by account</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 btn-active" data-testid="add-signup-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Signup
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Record New Signup</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Date Picker */}
              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="signup-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(form.date, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(date) => date && setForm({ ...form, date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="account">Account Name *</Label>
                <Input
                  id="account"
                  placeholder="e.g., Bank Account, Investment Account"
                  value={form.account_name}
                  onChange={(e) => setForm({ ...form, account_name: e.target.value })}
                  data-testid="signup-account-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client">Client Name *</Label>
                <Input
                  id="client"
                  placeholder="Client's full name"
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  data-testid="signup-client-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount ($) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="signup-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Additional notes..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  data-testid="signup-description-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={saving}
                data-testid="signup-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : "Record Signup"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-700 font-mono">${totalAmount.toLocaleString()}</p>
                <p className="text-sm text-emerald-600">Total Amount</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{signups.length}</p>
                <p className="text-sm text-slate-500">Total Signups</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  ${signups.length > 0 ? (totalAmount / signups.length).toFixed(2) : "0.00"}
                </p>
                <p className="text-sm text-slate-500">Avg. per Signup</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account Breakdown */}
      {Object.keys(accountGroups).length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">By Account</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(accountGroups).map(([name, data]) => (
                <div key={name} className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-900">{name}</p>
                  <div className="flex justify-between mt-2 text-sm">
                    <span className="text-slate-500">{data.count} signups</span>
                    <span className="font-mono font-medium text-emerald-600">${data.amount.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signup List */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Recent Signups</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Account</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Running Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {signups.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-500">
                      No signups recorded yet
                    </td>
                  </tr>
                ) : (
                  signups.map((signup) => (
                    <tr key={signup.id} data-testid={`signup-row-${signup.id}`}>
                      <td className="font-medium">
                        {formatTableDateSydney(signup.date)}
                      </td>
                      <td>{signup.account_name}</td>
                      <td>{signup.client_name}</td>
                      <td className="font-mono text-emerald-600 font-medium">
                        ${signup.amount?.toLocaleString()}
                      </td>
                      <td className="font-mono text-slate-500">
                        ${signup.running_total?.toLocaleString()}
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(signup.id)}
                          data-testid={`delete-signup-${signup.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
